import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Bell, Briefcase, GraduationCap, LayoutDashboard, LogOut, Menu, Users,
  Wallet, Search, FileCheck, CreditCard, Settings, BookOpen, Kanban, CalendarCheck,
  ClipboardList, IndianRupee, UserCheck, Globe, FileText, BarChart3, LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

export const NAV_BY_ROLE = {
  employer: [
    { to: "/employer", label: "Overview", icon: LayoutDashboard },
    { to: "/employer/jobs", label: "Jobs", icon: Briefcase },
    { to: "/employer/pipeline", label: "ATS Pipeline", icon: Kanban },
    { to: "/employer/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/employer/talent", label: "Talent Search", icon: Search },
    { to: "/employer/people", label: "Employees & HRMS", icon: Users },
    { to: "/employer/documents", label: "Documents", icon: FileText },
    { to: "/employer/team", label: "Team", icon: UserCheck },
    { to: "/employer/plans", label: "Subscription", icon: CreditCard },
    { to: "/employer/billing", label: "Wallet & Billing", icon: Wallet },
  ],
  candidate: [
    { to: "/candidate", label: "Overview", icon: LayoutDashboard },
    { to: "/jobs", label: "Find Jobs", icon: Briefcase },
    { to: "/candidate/applications", label: "My Applications", icon: FileCheck },
    { to: "/candidate/cv", label: "CV & AI Tools", icon: ClipboardList },
    { to: "/candidate/learning", label: "Learning", icon: GraduationCap },
  ],
  partner: [
    { to: "/partner", label: "Overview", icon: LayoutDashboard },
    { to: "/partner/openings", label: "Openings", icon: Briefcase },
    { to: "/partner/submissions", label: "Submissions", icon: FileCheck },
    { to: "/partner/earnings", label: "Earnings", icon: IndianRupee },
    { to: "/partner/crm", label: "Leads CRM", icon: Users },
    { to: "/partner/team", label: "Team", icon: UserCheck },
    { to: "/partner/subscription", label: "Subscription", icon: CreditCard },
  ],
  institute: [{ to: "/institute", label: "Institute Hub", icon: GraduationCap }],
  consultant: [{ to: "/consultant", label: "Consultant Desk", icon: UserCheck }],
  admin: [
    { to: "/admin", label: "Command Center", icon: LayoutDashboard },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/admin/jobs", label: "Job Reviews", icon: Briefcase },
    { to: "/admin/users", label: "Users & Orgs", icon: Users },
    { to: "/admin/courses", label: "Course Reviews", icon: BookOpen },
    { to: "/admin/payouts", label: "Payouts", icon: IndianRupee },
    { to: "/admin/pricing", label: "Pricing & Config", icon: Settings },
    { to: "/admin/cms", label: "CMS", icon: Globe },
  ],
  employee: [{ to: "/employee", label: "My Workspace", icon: CalendarCheck }],
};

export function navFor(user, extras) {
  const r = user?.role;
  if (["super_admin", "admin", "finance_admin", "hr_admin", "support"].includes(r)) return NAV_BY_ROLE.admin;
  if (["employer_owner", "employer_admin", "hiring_manager", "interviewer"].includes(r)) {
    let items = NAV_BY_ROLE.employer;
    if (r === "interviewer") {
      items = items.filter((i) => ["/employer", "/employer/jobs", "/employer/pipeline"].includes(i.to));
    } else if (r === "hiring_manager") {
      items = items.filter((i) => !["/employer/plans", "/employer/billing", "/employer/team"].includes(i.to));
    } else if (r === "employer_admin") {
      items = items.filter((i) => i.to !== "/employer/team");
    }
    return items;
  }
  if (["franchise_owner", "franchise_recruiter"].includes(r)) {
    if (r === "franchise_recruiter") {
      return NAV_BY_ROLE.partner.filter((i) => !["/partner/team", "/partner/subscription"].includes(i.to));
    }
    return NAV_BY_ROLE.partner;
  }
  if (["institute_owner", "trainer"].includes(r)) return NAV_BY_ROLE.institute;
  if (r === "hr_consultant") return NAV_BY_ROLE.consultant;
  if (r === "candidate") {
    const items = [...NAV_BY_ROLE.candidate];
    if (extras?.employee) items.push({ to: "/employee", label: "My Workspace", icon: CalendarCheck });
    return items;
  }
  return NAV_BY_ROLE.employee;
}

const PRIMARY_PATHS = {
  employer: ["/employer", "/employer/jobs", "/employer/pipeline", "/employer/people"],
  candidate: ["/candidate", "/jobs", "/candidate/applications", "/candidate/learning"],
  admin: ["/admin", "/admin/jobs", "/admin/users", "/admin/payouts"],
  partner: ["/partner", "/partner/openings", "/partner/submissions", "/partner/earnings"],
};

function navGroupFor(user) {
  const r = user?.role;
  if (["super_admin", "admin", "finance_admin", "hr_admin", "support"].includes(r)) return "admin";
  if (["employer_owner", "employer_admin", "hiring_manager", "interviewer"].includes(r)) return "employer";
  if (["franchise_owner", "franchise_recruiter"].includes(r)) return "partner";
  if (r === "candidate") return "candidate";
  return "other";
}

export function MobileTabBar() {
  const { user, extras, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  if (!user) return null;

  const items = [...navFor(user, extras), { to: "/support", label: "Support", icon: LifeBuoy }];
  const primaryPaths = PRIMARY_PATHS[navGroupFor(user)] || items.slice(0, 4).map((i) => i.to);
  const primaryItems = items.filter((i) => primaryPaths.includes(i.to));
  const moreItems = items.filter((i) => !primaryPaths.includes(i.to));

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border/60 flex items-stretch pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-nav"
        aria-label="Primary"
      >
        {primaryItems.map((item) => (
          <NavLink
            key={item.to + item.label}
            to={item.to}
            end={item.to.split("/").length <= 2}
            data-testid={`mnav-${item.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[60px] text-[10px] font-semibold transition-colors ${
                isActive ? "text-[#2563EB]" : "text-muted-foreground"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            {item.label.split(" ")[0]}
          </NavLink>
        ))}
        <button
          onClick={() => setMoreOpen(true)}
          data-testid="mnav-more"
          className="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[60px] text-[10px] font-semibold text-muted-foreground"
        >
          <Menu className="w-5 h-5" />
          More
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[env(safe-area-inset-bottom)]" data-testid="mobile-more-sheet" aria-describedby={undefined}>
          <SheetHeader>
            <SheetTitle>Menu</SheetTitle>
          </SheetHeader>
          <div className="py-2 space-y-1 max-h-[60vh] overflow-y-auto">
            {moreItems.map((item) => (
              <NavLink
                key={item.to + item.label}
                to={item.to}
                end={item.to.split("/").length <= 2}
                onClick={() => setMoreOpen(false)}
                data-testid={`mnav-sheet-${item.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-[#2563EB]/10 text-[#2563EB]" : "text-[#0B1B3D] hover:bg-muted"
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="border-t border-border/60 pt-4 mt-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{(user?.role || "").replace(/_/g, " ")}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="mnav-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
